import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { IdeaTask, TaskStatus } from '@/lib/types';
import { TaskCard, SortableTaskCard } from './TaskCard';
import { BlockedReasonModal, DoneNoteModal } from './TaskModals';
import { Circle, Clock, AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const WIP_LIMIT = 3;

interface PlanKanbanProps {
  tasks: IdeaTask[];
  onUpdateTask: (taskId: string, updates: Partial<IdeaTask>) => Promise<boolean>;
  onDeleteTask: (taskId: string) => Promise<boolean>;
  onReorderTasks: (taskId: string, newPosition: number, newStatus: TaskStatus) => Promise<boolean>;
  onEditTask?: (task: IdeaTask) => void;
}

const COLUMNS: { status: TaskStatus; label: string; icon: typeof Circle }[] = [
  { status: 'next', label: 'Next', icon: Circle },
  { status: 'doing', label: 'Doing', icon: Clock },
  { status: 'blocked', label: 'Blocked', icon: AlertCircle },
  { status: 'done', label: 'Done', icon: CheckCircle2 },
];

// Droppable column component
function DroppableColumn({
  status,
  label,
  icon: Icon,
  tasks,
  doingCount,
  wipLimit,
  onDeleteTask,
  onEditTask,
}: {
  status: TaskStatus;
  label: string;
  icon: typeof Circle;
  tasks: IdeaTask[];
  doingCount: number;
  wipLimit: number;
  onDeleteTask: (taskId: string) => Promise<boolean>;
  onEditTask?: (task: IdeaTask) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const isDoingColumn = status === 'doing';
  const isAtLimit = isDoingColumn && doingCount >= wipLimit;

  return (
    <div className="flex flex-col min-w-[280px] md:min-w-[320px] shrink-0">
      {/* Column header */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 mb-3 rounded-lg',
          status === 'blocked' ? 'bg-destructive/10' : 'bg-muted/50',
          isAtLimit && 'bg-amber-100 dark:bg-amber-900/30'
        )}
      >
        <Icon
          className={cn(
            'h-4 w-4',
            status === 'done' && 'text-primary',
            status === 'blocked' && 'text-destructive',
            status === 'doing' && 'text-primary'
          )}
        />
        <span className="text-sm font-medium">{label}</span>
        {isDoingColumn && (
          <span
            className={cn(
              'text-xs ml-1',
              isAtLimit ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
            )}
          >
            ({doingCount}/{wipLimit})
          </span>
        )}
        {isAtLimit && <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />}
        <span className="text-xs text-muted-foreground ml-auto">{tasks.length}</span>
      </div>

      {/* Droppable area - this is the actual drop target */}
      <div
        ref={setNodeRef}
        data-droppable-id={status}
        className={cn(
          'flex-1 rounded-lg transition-colors duration-150 p-2',
          'min-h-[200px]', // Ensure sufficient drop area even when empty
          isOver && !isAtLimit && 'bg-primary/10 ring-2 ring-primary/30',
          isOver && isAtLimit && 'bg-amber-100/50 ring-2 ring-amber-400/50 dark:bg-amber-900/30'
        )}
      >
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {tasks.length === 0 ? (
              <div 
                className={cn(
                  'text-xs text-center py-12 italic border-2 border-dashed rounded-lg min-h-[160px] flex items-center justify-center',
                  isOver && !isAtLimit && 'border-primary/40 text-primary/60 bg-primary/5',
                  isOver && isAtLimit && 'border-amber-400/40 text-amber-600',
                  !isOver && 'border-muted/50 text-muted-foreground/50'
                )}
              >
                {isOver && isAtLimit ? 'WIP limit reached' : 'Drop here'}
              </div>
            ) : (
              tasks.map((task) => (
                <SortableTaskCard key={task.id} task={task} onDelete={onDeleteTask} onEdit={onEditTask} />
              ))
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
}

export function PlanKanban({ tasks, onUpdateTask, onDeleteTask, onReorderTasks, onEditTask }: PlanKanbanProps) {
  const [activeTask, setActiveTask] = useState<IdeaTask | null>(null);
  const [pendingMove, setPendingMove] = useState<{ task: IdeaTask; targetStatus: TaskStatus } | null>(null);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [overColumn, setOverColumn] = useState<TaskStatus | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getTasksByStatus = (status: TaskStatus) =>
    tasks.filter((t) => t.status === status).sort((a, b) => a.position - b.position);

  const doingCount = getTasksByStatus('doing').length;

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) {
      setOverColumn(null);
      return;
    }
    
    const overId = String(over.id);
    // Check if over a column directly
    if (['next', 'doing', 'blocked', 'done'].includes(overId)) {
      setOverColumn(overId as TaskStatus);
    } else {
      // Over a task - find its column
      const overTask = tasks.find((t) => t.id === overId);
      setOverColumn(overTask?.status || null);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task || null);
    setOverColumn(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    setOverColumn(null);

    if (!over) return;

    const activeTask = tasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    // Determine target status from drop location
    let targetStatus: TaskStatus = activeTask.status;
    const overId = String(over.id);

    // Check if dropped on a column (droppable zone)
    if (['next', 'doing', 'blocked', 'done'].includes(overId)) {
      targetStatus = overId as TaskStatus;
    } else {
      // Dropped on a task - find its status
      const overTask = tasks.find((t) => t.id === overId);
      if (overTask) {
        targetStatus = overTask.status;
      }
    }

    // Handle status change
    if (targetStatus !== activeTask.status) {
      await handleStatusChange(activeTask, targetStatus);
    } else {
      // Just reordering within same column
      const columnTasks = getTasksByStatus(targetStatus);
      const activeIndex = columnTasks.findIndex((t) => t.id === active.id);
      const overIndex = columnTasks.findIndex((t) => t.id === overId);

      if (overIndex !== -1 && activeIndex !== overIndex) {
        await onReorderTasks(activeTask.id, overIndex, targetStatus);
      }
    }
  };

  const handleStatusChange = async (task: IdeaTask, newStatus: TaskStatus) => {
    // WIP limit check for 'doing'
    if (newStatus === 'doing' && task.status !== 'doing') {
      if (doingCount >= WIP_LIMIT) {
        toast.error('Finish something. Then start something.', {
          description: `WIP limit: max ${WIP_LIMIT} tasks in Doing`,
        });
        return;
      }
    }

    // Blocked requires reason
    if (newStatus === 'blocked') {
      setPendingMove({ task, targetStatus: newStatus });
      setShowBlockedModal(true);
      return;
    }

    // Done prompts for note
    if (newStatus === 'done') {
      setPendingMove({ task, targetStatus: newStatus });
      setShowDoneModal(true);
      return;
    }

    // Direct move
    const columnTasks = getTasksByStatus(newStatus);
    const newPosition = columnTasks.length; // Add to end
    await onReorderTasks(task.id, newPosition, newStatus);
  };

  const handleBlockedConfirm = async (reason: string) => {
    if (!pendingMove) return;
    const columnTasks = getTasksByStatus('blocked');
    await onUpdateTask(pendingMove.task.id, {
      status: 'blocked',
      blocked_reason: reason,
      position: columnTasks.length,
    });
    setPendingMove(null);
  };

  const handleDoneConfirm = async (note: string, evidence: string) => {
    if (!pendingMove) return;
    const columnTasks = getTasksByStatus('done');
    await onUpdateTask(pendingMove.task.id, {
      status: 'done',
      done_note: note || null,
      evidence: evidence || pendingMove.task.evidence,
      position: columnTasks.length,
    });
    setPendingMove(null);
  };

  const handleDoneSkip = async () => {
    if (!pendingMove) return;
    const columnTasks = getTasksByStatus('done');
    await onReorderTasks(pendingMove.task.id, columnTasks.length, 'done');
    setPendingMove(null);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {/* Board container with horizontal scroll - columns never wrap */}
        <div className="overflow-x-auto overflow-y-hidden pb-4">
          <div className="flex flex-nowrap gap-6 p-1">
            {COLUMNS.map(({ status, label, icon }) => (
              <DroppableColumn
                key={status}
                status={status}
                label={label}
                icon={icon}
                tasks={getTasksByStatus(status)}
                doingCount={doingCount}
                wipLimit={WIP_LIMIT}
                onDeleteTask={onDeleteTask}
                onEditTask={onEditTask}
              />
            ))}
          </div>
        </div>

        <DragOverlay>
          {activeTask && (
            <div className="opacity-95 rotate-2 scale-105">
              <TaskCard task={activeTask} onDelete={async () => false} isDragging />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Modals */}
      <BlockedReasonModal
        open={showBlockedModal}
        onOpenChange={(open) => {
          setShowBlockedModal(open);
          if (!open) setPendingMove(null);
        }}
        onConfirm={handleBlockedConfirm}
        taskTitle={pendingMove?.task.title || ''}
      />

      <DoneNoteModal
        open={showDoneModal}
        onOpenChange={(open) => {
          setShowDoneModal(open);
          if (!open) setPendingMove(null);
        }}
        onConfirm={handleDoneConfirm}
        onSkip={handleDoneSkip}
        taskTitle={pendingMove?.task.title || ''}
      />
    </>
  );
}
